import { useState, useCallback, useRef } from 'react';
import { useInvestigate } from '../api/osint';
import type { InvestigationResult } from '../api/osint';
import InvestigationProgress from '../components/investigation/InvestigationProgress';
import InvestigationRealResults from '../components/investigation/InvestigationRealResults';
import type { InvestigationRealResultsRef } from '../components/investigation/InvestigationRealResults';

// ── Types ──

type InvestigationType = 'person' | 'organization' | 'domain' | 'email' | 'ip' | 'crypto' | 'phone';
type DepthLevel = 'rapide' | 'standard' | 'approfondie';

interface ModuleOption {
  id: string;
  label: string;
  description: string;
}

interface ModuleRunStatus {
  id: string;
  label: string;
  status: 'en_attente' | 'en_cours' | 'termine' | 'erreur';
  resultCount?: number;
}

// ── Constantes ──

const INVESTIGATION_TYPES: Array<{ value: InvestigationType; label: string; icon: string }> = [
  { value: 'person', label: 'Personne', icon: 'user' },
  { value: 'organization', label: 'Organisation', icon: 'building' },
  { value: 'domain', label: 'Domaine', icon: 'globe' },
  { value: 'email', label: 'Email', icon: 'mail' },
  { value: 'ip', label: 'Adresse IP', icon: 'server' },
  { value: 'crypto', label: 'Crypto-wallet', icon: 'bitcoin' },
  { value: 'phone', label: 'Téléphone', icon: 'phone' },
];

const OSINT_MODULES: ModuleOption[] = [
  { id: 'domain_intel', label: 'Domain Intel', description: 'WHOIS, DNS, SSL, crt.sh, VirusTotal, SecurityTrails, sous-domaines, technologies' },
  { id: 'social', label: 'Réseaux sociaux (80+)', description: 'Facebook, X/Twitter, Instagram, Snapchat, TikTok, YouTube, LinkedIn, Pinterest, Reddit, Telegram, WhatsApp, Discord, Twitch, Steam, VK, Mastodon, Threads, BeReal, Signal, Viber, WeChat, GitHub, GitLab, Medium, Quora, Tumblr, Flickr, DeviantArt, SoundCloud, Spotify, Deezer, Bandcamp, MySpace, OK.ru, Badoo, Tinder, Gravatar, Keybase, About.me, BitBucket, StackOverflow, HackerOne, Bugcrowd, Patreon, Substack, Linktree, Cash App, Venmo, PayPal...' },
  { id: 'email', label: 'Email OSINT', description: 'HIBP, EmailRep, DeHashed, Hunter.io, fuites de données, paste sites, validation MX' },
  { id: 'phone', label: 'Téléphone OSINT', description: 'NumVerify, Truecaller, HLRLOOKUP, Infobel, Pages Blanches, réseaux sociaux liés, opérateur, géolocalisation, WhatsApp, Telegram, Viber, Signal' },
  { id: 'network', label: 'Réseau & Threat Intel', description: 'Shodan, Censys, AbuseIPDB, VirusTotal, OTX, ThreatFox, URLhaus, GreyNoise, ports scan' },
  { id: 'crypto', label: 'Crypto', description: 'Etherscan, Blockchain.com, Chainalysis, Wallet Explorer, threat intel' },
  { id: 'darkweb', label: 'Dark Web & Paste Sites', description: 'Pastebin, Ghostbin, Ahmia, IntelX, dark web mentions, fuites' },
  { id: 'documents', label: 'Documents & Archives', description: 'Archive.org, Google Scholar, métadonnées, code (GitHub, GitLab, Bitbucket)' },
  { id: 'search_engines', label: 'Moteurs de recherche (10)', description: 'DuckDuckGo, Bing, Brave, Google News, Yandex, Startpage, Qwant, Mojeek, Ecosia, SearX' },
  { id: 'communities', label: 'Communautés', description: 'Reddit, Hacker News, Wikipedia FR/EN, forums, 4chan archives' },
];

const DEPTH_LEVELS: Array<{ value: DepthLevel; label: string; description: string; duration: string }> = [
  { value: 'rapide', label: 'Rapide', description: 'Résultats de surface, sources principales', duration: '~2 min' },
  { value: 'standard', label: 'Standard', description: 'Analyse complète des sources sélectionnées', duration: '~10 min' },
  { value: 'approfondie', label: 'Approfondie', description: 'Exploration récursive, corrélation croisée', duration: '~30 min' },
];

// Map frontend depth to API depth
const DEPTH_API_MAP: Record<DepthLevel, 'shallow' | 'standard' | 'deep'> = {
  rapide: 'shallow',
  standard: 'standard',
  approfondie: 'deep',
};

const TYPE_ICONS: Record<string, JSX.Element> = {
  user: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  ),
  building: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  ),
  globe: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
    </svg>
  ),
  mail: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
  ),
  server: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
    </svg>
  ),
  bitcoin: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  phone: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
    </svg>
  ),
};

// ── Composant principal ──

export default function InvestigationWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [investigationType, setInvestigationType] = useState<InvestigationType | null>(null);
  const [target, setTarget] = useState('');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [depth, setDepth] = useState<DepthLevel>('standard');
  const [enableCorrelation, setEnableCorrelation] = useState(true);
  const [enableSimulation, setEnableSimulation] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [moduleStatuses, setModuleStatuses] = useState<ModuleRunStatus[]>([]);
  const [investigationResult, setInvestigationResult] = useState<InvestigationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const resultsRef = useRef<InvestigationRealResultsRef>(null);

  const investigateMut = useInvestigate();

  const steps = ['Cible', 'Sources', 'Profondeur', 'Lancement', 'Résultats'];

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return investigationType !== null && target.trim().length > 0;
      case 1:
        return selectedModules.length > 0;
      case 2:
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const toggleModule = (id: string) => {
    setSelectedModules((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const selectAllModules = () => {
    setSelectedModules(OSINT_MODULES.map((m) => m.id));
  };

  const launchInvestigation = useCallback(async () => {
    if (!investigationType) return;

    setIsRunning(true);
    setCurrentStep(4);
    setError(null);
    setInvestigationResult(null);

    // Show progress — set all modules to "en_cours" since the backend runs everything at once
    const statuses: ModuleRunStatus[] = selectedModules.map((id) => ({
      id,
      label: OSINT_MODULES.find((m) => m.id === id)?.label || id,
      status: 'en_cours' as const,
    }));
    setModuleStatuses(statuses);

    // Map investigation type for the API
    const typeMap: Record<InvestigationType, string> = {
      person: 'person',
      organization: 'organization',
      domain: 'domain',
      email: 'email',
      ip: 'ip',
      crypto: 'crypto',
      phone: 'phone',
    };

    try {
      const result = await investigateMut.mutateAsync({
        type: typeMap[investigationType] as any,
        target: target.trim(),
        depth: DEPTH_API_MAP[depth],
        modules: selectedModules,
      });

      setInvestigationResult(result);

      // Mark all modules as completed with result counts from findings
      const findings = result.findings;
      setModuleStatuses((prev) =>
        prev.map((m) => ({
          ...m,
          status: result.status === 'completed' ? 'termine' as const : 'erreur' as const,
          resultCount: findings
            ? (findings.timeline?.length || 0) + (findings.related_entities?.length || 0)
            : 0,
        }))
      );

      if (result.error) {
        setError(result.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Investigation failed');
      setModuleStatuses((prev) =>
        prev.map((m) => ({ ...m, status: 'erreur' as const }))
      );
    } finally {
      setIsRunning(false);
    }
  }, [investigationType, target, depth, selectedModules, investigateMut]);

  const handleCancel = () => {
    setIsRunning(false);
    setCurrentStep(3);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-100">Investigation</h1>
        <p className="text-sm text-gray-500 mt-1">
          Assistant d'investigation OSINT pas à pas
        </p>
      </div>

      {/* Barre de progression des etapes */}
      <div className="flex items-center mb-8">
        {steps.map((step, i) => (
          <div key={step} className="flex items-center flex-1">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  i < currentStep
                    ? 'bg-cyber-cyan text-surface-primary'
                    : i === currentStep
                    ? 'bg-cyber-cyan/20 text-cyber-cyan border-2 border-cyber-cyan'
                    : 'bg-surface-tertiary text-gray-500'
                }`}
              >
                {i < currentStep ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`text-sm font-medium hidden sm:block ${
                  i <= currentStep ? 'text-gray-200' : 'text-gray-500'
                }`}
              >
                {step}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-3 rounded ${
                  i < currentStep ? 'bg-cyber-cyan' : 'bg-surface-tertiary'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Contenu de l'etape */}
      <div className="bg-surface-secondary border border-border-subtle rounded-xl p-6">
        {/* Etape 1: Cible */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-200 mb-1">
                Type d'investigation
              </h2>
              <p className="text-sm text-gray-500">
                Sélectionnez le type de cible et saisissez l'identifiant.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {INVESTIGATION_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setInvestigationType(type.value)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${
                    investigationType === type.value
                      ? 'border-cyber-cyan bg-cyber-cyan/10 text-cyber-cyan'
                      : 'border-border-subtle bg-surface-tertiary text-gray-400 hover:text-gray-200 hover:border-gray-600'
                  }`}
                >
                  {TYPE_ICONS[type.icon]}
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Cible
              </label>
              <input
                type="text"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                placeholder={
                  investigationType === 'person'
                    ? 'Nom complet ou identifiant...'
                    : investigationType === 'domain'
                    ? 'example.com'
                    : investigationType === 'email'
                    ? 'user@example.com'
                    : investigationType === 'ip'
                    ? '192.168.1.1'
                    : investigationType === 'crypto'
                    ? '0x... ou bc1...'
                    : investigationType === 'phone'
                    ? '+33 6 12 34 56 78'
                    : 'Saisissez la cible...'
                }
                className="w-full rounded-lg bg-surface-tertiary border border-border-subtle px-4 py-3 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50 focus:ring-1 focus:ring-cyber-cyan/25"
              />
            </div>
          </div>
        )}

        {/* Etape 2: Sources */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-200 mb-1">
                  Modules OSINT
                </h2>
                <p className="text-sm text-gray-500">
                  Sélectionnez les sources de données à utiliser.
                </p>
              </div>
              <button
                onClick={selectAllModules}
                className="text-xs text-cyber-cyan hover:text-cyan-300"
              >
                Tout sélectionner
              </button>
            </div>

            <div className="space-y-2">
              {OSINT_MODULES.map((mod) => (
                <label
                  key={mod.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedModules.includes(mod.id)
                      ? 'border-cyber-cyan/40 bg-cyber-cyan/5'
                      : 'border-border-subtle bg-surface-tertiary hover:border-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedModules.includes(mod.id)}
                    onChange={() => toggleModule(mod.id)}
                    className="w-4 h-4 rounded border-gray-600 bg-surface-tertiary text-cyber-cyan focus:ring-cyber-cyan/25 focus:ring-offset-0"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-200">
                      {mod.label}
                    </div>
                    <div className="text-xs text-gray-500">{mod.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Etape 3: Profondeur */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-200 mb-1">
                Profondeur d'analyse
              </h2>
              <p className="text-sm text-gray-500">
                Choisissez le niveau de détail de l'investigation.
              </p>
            </div>

            <div className="space-y-3">
              {DEPTH_LEVELS.map((level) => (
                <button
                  key={level.value}
                  onClick={() => setDepth(level.value)}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    depth === level.value
                      ? 'border-cyber-cyan bg-cyber-cyan/10'
                      : 'border-border-subtle bg-surface-tertiary hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-sm font-semibold ${
                        depth === level.value ? 'text-cyber-cyan' : 'text-gray-200'
                      }`}
                    >
                      {level.label}
                    </span>
                    <span className="text-xs text-gray-500">{level.duration}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{level.description}</p>
                </button>
              ))}
            </div>

            <div className="space-y-3 pt-4 border-t border-border-subtle">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="text-sm font-medium text-gray-200">
                    Corrélation croisée
                  </div>
                  <div className="text-xs text-gray-500">
                    Croiser les résultats entre modules
                  </div>
                </div>
                <button
                  onClick={() => setEnableCorrelation(!enableCorrelation)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    enableCorrelation ? 'bg-cyber-cyan' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                      enableCorrelation ? 'translate-x-5' : ''
                    }`}
                  />
                </button>
              </label>

              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="text-sm font-medium text-gray-200">
                    Simulation de propagation
                  </div>
                  <div className="text-xs text-gray-500">
                    Simuler l'impact des relations découvertes
                  </div>
                </div>
                <button
                  onClick={() => setEnableSimulation(!enableSimulation)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    enableSimulation ? 'bg-cyber-cyan' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                      enableSimulation ? 'translate-x-5' : ''
                    }`}
                  />
                </button>
              </label>
            </div>
          </div>
        )}

        {/* Etape 4: Lancement */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-200 mb-1">
                Récapitulatif
              </h2>
              <p className="text-sm text-gray-500">
                Vérifiez les paramètres avant de lancer l'investigation.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-sm text-gray-500">Type</span>
                <span className="text-sm font-medium text-gray-200">
                  {INVESTIGATION_TYPES.find((t) => t.value === investigationType)?.label}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-sm text-gray-500">Cible</span>
                <span className="text-sm font-medium text-cyber-cyan">{target}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-sm text-gray-500">Modules</span>
                <span className="text-sm font-medium text-gray-200">
                  {selectedModules.length} module{selectedModules.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-sm text-gray-500">Profondeur</span>
                <span className="text-sm font-medium text-gray-200">
                  {DEPTH_LEVELS.find((d) => d.value === depth)?.label}
                </span>
              </div>
              <div className="flex justify-between py-3 border-b border-border-subtle">
                <span className="text-sm text-gray-500">Corrélation</span>
                <span className={`text-sm font-medium ${enableCorrelation ? 'text-green-400' : 'text-gray-500'}`}>
                  {enableCorrelation ? 'Activée' : 'Désactivée'}
                </span>
              </div>
              <div className="flex justify-between py-3">
                <span className="text-sm text-gray-500">Simulation</span>
                <span className={`text-sm font-medium ${enableSimulation ? 'text-green-400' : 'text-gray-500'}`}>
                  {enableSimulation ? 'Activée' : 'Désactivée'}
                </span>
              </div>
            </div>

            <button
              onClick={launchInvestigation}
              disabled={investigateMut.isPending}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-cyber-cyan to-helios-600 text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {investigateMut.isPending ? 'Lancement en cours...' : "Lancer l'investigation"}
            </button>
          </div>
        )}

        {/* Etape 5: Resultats */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-200 mb-1">
                {isRunning ? "Investigation en cours..." : "Résultats de l'investigation"}
              </h2>
              <p className="text-sm text-gray-500">
                Cible : <span className="text-cyber-cyan">{target}</span>
                {investigationType && (
                  <span className="text-gray-600"> ({INVESTIGATION_TYPES.find(t => t.value === investigationType)?.label})</span>
                )}
              </p>
            </div>

            <InvestigationProgress
              modules={moduleStatuses}
              estimatedTimeRemaining={isRunning ? 10 : undefined}
              onCancel={handleCancel}
              onModuleClick={(moduleId) => {
                resultsRef.current?.switchTab(moduleId as any);
              }}
            />

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                {error}
              </div>
            )}

            {!isRunning && investigationResult && investigationResult.findings && (
              <InvestigationRealResults
                ref={resultsRef}
                result={investigationResult}
                investigationType={investigationType || 'person'}
              />
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      {currentStep < 4 && (
        <div className="flex justify-between mt-6">
          <button
            onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
            disabled={currentStep === 0}
            className="px-6 py-2.5 rounded-lg border border-border-subtle text-gray-400 hover:text-gray-200 hover:bg-surface-tertiary disabled:opacity-30 disabled:cursor-not-allowed text-sm font-medium transition-colors"
          >
            Précédent
          </button>

          {currentStep < 3 ? (
            <button
              onClick={() => setCurrentStep((s) => s + 1)}
              disabled={!canProceed()}
              className="px-6 py-2.5 rounded-lg bg-cyber-cyan text-surface-primary hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              Suivant
            </button>
          ) : null}
        </div>
      )}

      {/* Retour depuis les resultats */}
      {currentStep === 4 && !isRunning && (
        <div className="flex justify-between mt-6">
          <button
            onClick={() => {
              setCurrentStep(0);
              setInvestigationResult(null);
              setModuleStatuses([]);
              setError(null);
            }}
            className="px-6 py-2.5 rounded-lg border border-border-subtle text-gray-400 hover:text-gray-200 hover:bg-surface-tertiary text-sm font-medium transition-colors"
          >
            Nouvelle investigation
          </button>
        </div>
      )}
    </div>
  );
}
